import React, { useState, useEffect } from 'react';
import { Box, Typography, Grid, Paper, Snackbar, Alert, TextField, FormControl, InputLabel, Select, MenuItem, Button, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, Skeleton, Divider, Menu } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ReportService from '../../../api/services/reportService';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import ReplayIcon from '@mui/icons-material/Replay';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { formatCurrency } from '../../../utils/currency';
import { standardFilterInputSx, standardFilterSelectSx, standardResetButtonSx, standardSecondaryButtonSx } from '../../../components/common/tableStyles';

export default function PnlReportTab({ tenantId, isActive }) {
    const [pnlData, setPnlData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        startDate: '',
        endDate: '',
        period: 'monthly'
    });
    const [exportAnchorEl, setExportAnchorEl] = useState(null);
    const [isExporting, setIsExporting] = useState(false);

    useEffect(() => {
        if (isActive) {
            fetchPnlData();
        }
    }, [isActive, tenantId, filters]);

    const fetchPnlData = async (isExport = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const params = { ...filters };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            
            if (isExport) {
                params.noPaging = true;
            }
            
            const res = await ReportService.getPnlReport(tenantId, params);
            const reportData = res?.data || res;
            setPnlData(reportData || null);
        } catch (error) {
            console.error('Error fetching P&L report:', error);
            setError('Failed to load P&L report. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({ ...prev, [field]: value }));
    };

    const resetFilters = () => {
        setFilters({
            startDate: '',
            endDate: '',
            period: 'monthly'
        });
    };

    const handleExportClick = (event) => {
        setExportAnchorEl(event.currentTarget);
    };

    const handleExportClose = () => {
        setExportAnchorEl(null);
    };

    const handleExport = async (format) => {
        handleExportClose();
        setIsExporting(true);
        setError(null);
        try {
            const params = { ...filters, format: 'csv' };
            Object.keys(params).forEach(key => {
                if (params[key] === '' || params[key] === null || params[key] === undefined) delete params[key];
            });
            const res = await ReportService.exportPnlReport(tenantId, params);
            
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data);
            }
            
            const rows = csvText.split('\n')
                .map(row => row.split(',').map(cell => cell.replace(/"/g, '').trim()))
                .filter(row => row.length > 0 && row.some(cell => cell !== ''));
                
            const fileName = `PnL_Report_${new Date().toISOString().split('T')[0]}`;
            
            if (format === 'csv') {
                const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `${fileName}.csv`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                window.URL.revokeObjectURL(url);
            } else if (format === 'excel') {
                const ws = XLSX.utils.aoa_to_sheet(rows);
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, "PnL");
                XLSX.writeFile(wb, `${fileName}.xlsx`);
            } else if (format === 'pdf') {
                const doc = new jsPDF();
                const headers = rows[0] || [];
                const body = rows.slice(1) || [];
                
                doc.text("P&L Financial Report", 14, 15);
                autoTable(doc, {
                    head: [headers],
                    body: body,
                    startY: 20,
                    styles: { fontSize: 8, cellPadding: 2 },
                    headStyles: { fillColor: [63, 81, 181] }
                });
                doc.save(`${fileName}.pdf`);
            }
        } catch (err) {
            console.error(`Error exporting P&L report as ${format}:`, err);
            setError(`Failed to export ${format.toUpperCase()} report. Please try again.`);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <Box>
            <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={() => setError(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert severity="warning" onClose={() => setError(null)}>{error}</Alert>
            </Snackbar>

            <Paper sx={{ p: 2, mb: 3, borderRadius: '12px', border: '1px solid #edf1f6', bgcolor: '#ffffff', boxShadow: 'none' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="Start Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.startDate}
                            onChange={(e) => handleFilterChange('startDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            label="End Date"
                            type="date"
                            size="small"
                            fullWidth
                            InputLabelProps={{ shrink: true }}
                            value={filters.endDate}
                            onChange={(e) => handleFilterChange('endDate', e.target.value)}
                            sx={standardFilterInputSx}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl size="small" fullWidth>
                            <Select
                                value={filters.period}
                                displayEmpty
                                onChange={(e) => handleFilterChange('period', e.target.value)}
                                sx={standardFilterSelectSx}
                            >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                                <MenuItem value="yearly">Yearly</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                    {(filters.startDate !== '' || filters.endDate !== '' || filters.period !== 'monthly') && (
                        <Button variant="outlined" startIcon={<ReplayIcon sx={{ fontSize: 16 }} />} sx={standardResetButtonSx} onClick={resetFilters} disabled={isExporting}>Reset</Button>
                    )}
                    <Button 
                        variant="outlined" 
                        onClick={handleExportClick}
                        disabled={isExporting}
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={standardSecondaryButtonSx}
                    >
                        {isExporting ? 'Exporting...' : 'Export Report'}
                    </Button>
                    <Menu
                        anchorEl={exportAnchorEl}
                        open={Boolean(exportAnchorEl)}
                        onClose={handleExportClose}
                    >
                        <MenuItem onClick={() => handleExport('csv')}>Export as CSV</MenuItem>
                        <MenuItem onClick={() => handleExport('excel')}>Export as Excel</MenuItem>
                        <MenuItem onClick={() => handleExport('pdf')}>Export as PDF</MenuItem>
                    </Menu>
                </Box>
            </Paper>

            {pnlData && !isLoading && (
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(76, 175, 80, 0.05)', border: '1px solid rgba(76, 175, 80, 0.2)' }}>
                            <Typography variant="h5" color="success.main" fontWeight="bold">
                                {formatCurrency(pnlData.revenue?.total)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Gross Revenue / Income</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(244, 67, 54, 0.05)', border: '1px solid rgba(244, 67, 54, 0.2)' }}>
                            <Typography variant="h5" color="error.main" fontWeight="bold">
                                {formatCurrency(pnlData.expenses?.total)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Total Operating Expenses</Typography>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <Paper sx={{ p: 2, textAlign: 'center', borderRadius: 2, bgcolor: 'rgba(33, 150, 243, 0.05)', border: '1px solid rgba(33, 150, 243, 0.2)' }}>
                            <Typography variant="h5" color="primary.main" fontWeight="bold">
                                {formatCurrency(pnlData.netProfit)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">Net Profit / (Loss)</Typography>
                        </Paper>
                    </Grid>
                </Grid>
            )}

            <TableContainer component={Paper}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Account Category</TableCell>
                            <TableCell align="right">Amount</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {isLoading ? (
                            Array.from(new Array(6)).map((_, idx) => (
                                <TableRow key={idx}>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                    <TableCell><Skeleton animation="wave" /></TableCell>
                                </TableRow>
                            ))
                        ) : (
                            pnlData ? (
                                <>
                                    {/* Revenue Section */}
                                    <TableRow sx={{ bgcolor: 'rgba(76, 175, 80, 0.05)' }}>
                                        <TableCell colSpan={2}>
                                            <Typography variant="subtitle2" color="success.main" fontWeight="bold">REVENUE</Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Rooms</TableCell><TableCell align="right">{formatCurrency(pnlData.revenue?.room)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Food & Beverage</TableCell><TableCell align="right">{formatCurrency(pnlData.revenue?.fb)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Spa & Leisure</TableCell><TableCell align="right">{formatCurrency(pnlData.revenue?.spa)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Other Income</TableCell><TableCell align="right">{formatCurrency(pnlData.revenue?.other)}</TableCell></TableRow>
                                    <TableRow>
                                        <TableCell sx={{ pl: 4 }}><Typography fontWeight="bold">Total Revenue</Typography></TableCell>
                                        <TableCell align="right"><Typography fontWeight="bold" color="success.main">{formatCurrency(pnlData.revenue?.total)}</Typography></TableCell>
                                    </TableRow>

                                    {/* Expenses Section */}
                                    <TableRow sx={{ bgcolor: 'rgba(244, 67, 54, 0.05)' }}>
                                        <TableCell colSpan={2}>
                                            <Typography variant="subtitle2" color="error.main" fontWeight="bold" sx={{ mt: 1 }}>OPERATING EXPENSES</Typography>
                                        </TableCell>
                                    </TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Cost of Goods Sold (COGS)</TableCell><TableCell align="right">{formatCurrency(pnlData.expenses?.cogs)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Payroll & Labor</TableCell><TableCell align="right">{formatCurrency(pnlData.expenses?.payroll)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Utilities</TableCell><TableCell align="right">{formatCurrency(pnlData.expenses?.utilities)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Marketing & Sales</TableCell><TableCell align="right">{formatCurrency(pnlData.expenses?.marketing)}</TableCell></TableRow>
                                    <TableRow hover><TableCell sx={{ pl: 4 }}>Other Expenses</TableCell><TableCell align="right">{formatCurrency(pnlData.expenses?.other)}</TableCell></TableRow>
                                    <TableRow>
                                        <TableCell sx={{ pl: 4 }}><Typography fontWeight="bold">Total Expenses</Typography></TableCell>
                                        <TableCell align="right"><Typography fontWeight="bold" color="error.main">{formatCurrency(pnlData.expenses?.total)}</Typography></TableCell>
                                    </TableRow>

                                    {/* Net Profit Section */}
                                    <TableRow sx={{ bgcolor: 'rgba(33, 150, 243, 0.05)' }}>
                                        <TableCell sx={{ pl: 4 }}><Typography variant="subtitle1" fontWeight="bold" color="primary.main">NET PROFIT</Typography></TableCell>
                                        <TableCell align="right"><Typography variant="subtitle1" fontWeight="bold" color="primary.main">{formatCurrency(pnlData.netProfit)}</Typography></TableCell>
                                    </TableRow>
                                </>
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 3 }}>
                                        <Typography color="text.secondary">No financial records found for the selected accounting period.</Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}
